import { useCallback, useEffect, useState } from 'react';
import { LayoutRectangle, Platform } from 'react-native';

interface PortalPositionOptions {
    anchorRef: React.RefObject<any>;
    dropdownRef: React.RefObject<any>;
    outsideClickRef?: React.RefObject<any>;
    open: boolean;
    onClose?: () => void;
    offsetY?: number;
}

/**
 * Hook to manage the position of a Portaled component (like a dropdown) on Web.
 * It provides high-performance synchronization using direct DOM manipulation
 * to avoid React render lag during scrolling.
 */
export function usePortalPosition({
    anchorRef,
    dropdownRef,
    outsideClickRef,
    open,
    onClose,
    offsetY = 6
}: PortalPositionOptions) {
    const [layout, setLayout] = useState<LayoutRectangle | null>(null);

    const measure = useCallback(() => {
        if (Platform.OS === 'web') {
            const node = anchorRef.current as any;
            if (node?.getBoundingClientRect) {
                const rect = node.getBoundingClientRect();
                setLayout({
                    x: rect.left,
                    y: rect.top,
                    width: rect.width,
                    height: rect.height
                });
            }
        } else {
            anchorRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
                setLayout({ x, y, width, height });
            });
        }
    }, [anchorRef]);

    const updatePosition = useCallback(() => {
        const anchorNode = anchorRef.current as any;
        const dropNode = dropdownRef.current as any;
        if (anchorNode?.getBoundingClientRect && dropNode) {
            const rect = anchorNode.getBoundingClientRect();
            dropNode.style.top = `${rect.top + rect.height + offsetY}px`;
            dropNode.style.left = `${rect.left}px`;
            dropNode.style.width = `${rect.width}px`;
        }
    }, [anchorRef, dropdownRef, offsetY]);

    // Sync scroll/resize
    useEffect(() => {
        if (!open || Platform.OS !== 'web') return;

        const handleScroll = () => updatePosition();
        const handleResize = () => measure();

        // Use capture to catch all sub-scrollers
        window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll, { capture: true });
            window.removeEventListener('resize', handleResize);
        };
    }, [open, updatePosition, measure]);

    // Handle outside click
    useEffect(() => {
        if (!open || Platform.OS !== 'web' || !onClose) return;

        const handleClickOutside = (event: any) => {
            const target = event.target;
            const containerNode = (outsideClickRef?.current || anchorRef.current) as any;
            const dropNode = dropdownRef.current as any;

            const isInside = (containerNode && containerNode.contains?.(target)) ||
                (dropNode && dropNode.contains?.(target));

            if (!isInside) {
                onClose();
            }
        };

        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, onClose, anchorRef, dropdownRef]);

    return { layout, measure };
}
